import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Button,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Paper,
  TextField,
  Autocomplete,
  Checkbox,
  Box,
  Typography,
  Stack,
  Pagination,
  useMediaQuery,
} from '@mui/material';
import { ArrowBack, Download as DownloadIcon } from '@mui/icons-material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { DataGrid } from '@mui/x-data-grid';
import useAxiosPrivate from '../../../../Hooks/useAxiosPrivate';
import styles from './ReportRunPage.module.css';

const ReportRunPage = ({ reportName, reportDescription, reportConfig }) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();

  const [filters, setFilters] = useState({});
  const [events, setEvents] = useState([]);
  const [roles, setRoles] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [hasRunReport, setHasRunReport] = useState(false);

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [sortModel, setSortModel] = useState([]);

  useEffect(() => {
    const loadFilterOptions = async () => {
      setIsLoadingOptions(true);
      try {
        const promises = reportConfig.filters.map(async (filter) => {
          if (filter.type === 'event') {
            const response = await axiosPrivate.get('/api/reports/events');
            setEvents(response.data || []);
          } else if (filter.type === 'role') {
            const response = await axiosPrivate.get('/api/reports/roles');
            setRoles(response.data || []);
          } else if (filter.type === 'certification') {
            const response = await axiosPrivate.get('/api/reports/certifications');
            setCertifications(response.data || []);
          }
        });
        await Promise.all(promises);
      } catch (error) {
        console.error('Failed to load filter options:', error);
        enqueueSnackbar('Failed to load filter options', { variant: 'error' });
      } finally {
        setIsLoadingOptions(false);
      }
    };

    if (reportConfig.filters && reportConfig.filters.length > 0) {
      loadFilterOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportConfig]);

  const validateFilters = () => {
    for (const filter of reportConfig.filters) {
      if (filter.required) {
        if (filter.type === 'event' && !filters.eventId) {
          enqueueSnackbar('Please select an event', { variant: 'warning' });
          return false;
        }
        if (filter.type === 'role' && filter.multiple && (!filters.roleIds || filters.roleIds.length === 0)) {
          enqueueSnackbar('Please select at least one role', { variant: 'warning' });
          return false;
        }
        if (filter.type === 'role' && !filter.multiple && !filters.roleId) {
          enqueueSnackbar('Please select a role', { variant: 'warning' });
          return false;
        }
        if (filter.type === 'certification' && (!filters.certificationIds || filters.certificationIds.length === 0)) {
          enqueueSnackbar('Please select at least one certification', { variant: 'warning' });
          return false;
        }
        if (filter.type === 'region' && filter.multiple && (!filters.regionIds || filters.regionIds.length === 0)) {
          enqueueSnackbar('Please select at least one region', { variant: 'warning' });
          return false;
        }
        if (filter.type === 'region' && !filter.multiple && !filters.regionId) {
          enqueueSnackbar('Please select a region', { variant: 'warning' });
          return false;
        }
      }
    }
    return true;
  };

  const runReport = async () => {
    if (!validateFilters()) return;

    setIsGenerating(true);
    setHasRunReport(false);
    setPaginationModel(prev => ({ ...prev, page: 0 }));

    try {
      const url = reportConfig.buildApiUrl(filters, 'json');
      const response = await axiosPrivate.get(url);

      // Tag each row with a stable unique id — DataGrid's getRowId needs one,
      // and report rows have no natural primary key.
      const data = (Array.isArray(response.data) ? response.data : [])
        .map((row, i) => ({ ...row, __rowId: i }));

      if (data.length === 0) {
        enqueueSnackbar('No data found for this report', { variant: 'info' });
      } else {
        enqueueSnackbar(`Report generated with ${data.length} records`, { variant: 'success' });
      }

      setReportData(data);
      setHasRunReport(true);
    } catch (error) {
      console.error('Report generation error:', error);
      enqueueSnackbar(
        error.response?.data?.error || 'Failed to generate report',
        { variant: 'error' }
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadReport = async (format) => {
    if (!validateFilters()) return;

    setIsGenerating(true);

    try {
      const url = reportConfig.buildApiUrl(filters, format);
      const response = await axiosPrivate.get(url, { responseType: 'blob' });

      const contentDisposition = response.headers['content-disposition'];
      let filename = `${reportName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.${format === 'csv' ? 'csv' : 'xlsx'}`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) filename = filenameMatch[1];
      }

      const blob = new Blob([response.data], {
        type: format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(link.href);

      enqueueSnackbar(`${format.toUpperCase()} report downloaded successfully`, { variant: 'success' });
    } catch (error) {
      console.error('Report download error:', error);
      enqueueSnackbar(
        error.response?.data?.error || 'Failed to download report',
        { variant: 'error' }
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const columns = useMemo(() => {
    if (!reportConfig?.columns) return [];
    return reportConfig.columns.map((col) => ({
      field: col.field,
      headerName: col.label,
      flex: 1,
      minWidth: 150,
    }));
  }, [reportConfig]);

  const totalCount = reportData.length;
  const pageCount = Math.ceil(totalCount / paginationModel.pageSize);
  const startIndex = paginationModel.page * paginationModel.pageSize;
  // Sort the ENTIRE result set (not just the current page) before paginating.
  // Numeric-aware so number columns sort correctly (not as strings).
  const sortedReportData = (() => {
    if (!sortModel.length) return reportData;
    const { field, sort } = sortModel[0];
    if (!field || !sort) return reportData;
    const dir = sort === 'desc' ? -1 : 1;
    const num = (v) => {
      const n = Number(String(v).replace(/[$,\s]/g, ''));
      return Number.isFinite(n) && String(v ?? '').trim() !== '' ? n : null;
    };
    return [...reportData].sort((a, b) => {
      const an = num(a?.[field]), bn = num(b?.[field]);
      if (an !== null && bn !== null) return (an - bn) * dir;
      const as = String(a?.[field] ?? '').toLowerCase();
      const bs = String(b?.[field] ?? '').toLowerCase();
      if (as < bs) return -1 * dir;
      if (as > bs) return 1 * dir;
      return 0;
    });
  })();
  const paginatedData = sortedReportData.slice(startIndex, startIndex + paginationModel.pageSize);

  const renderFilter = (filter, index) => {
    const filterLabel = filter.label.toUpperCase();

    switch (filter.type) {
      case 'event':
        return (
          <div key={index} className={styles.filterGroup}>
            <label className={styles.filterLabel}>{filterLabel}</label>
            <Autocomplete
              options={events}
              loading={isLoadingOptions}
              getOptionLabel={(option) => `${option.BP_Title}${option.BP_StartDate ? ' - ' + new Date(option.BP_StartDate).toLocaleDateString() : ''}`}
              isOptionEqualToValue={(option, value) => option.EventsID === value.EventsID}
              onChange={(e, value) => setFilters({ ...filters, eventId: value?.EventsID })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Event..."
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingOptions ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              disabled={isLoadingOptions}
              className={styles.filterInput}
            />
          </div>
        );

      case 'role':
        return (
          <div key={index} className={styles.filterGroup}>
            <label className={styles.filterLabel}>{filterLabel}</label>
            {filter.multiple ? (
              <Autocomplete
                multiple
                options={roles}
                loading={isLoadingOptions}
                disableCloseOnSelect
                getOptionLabel={(option) => option.RoleDisplayName}
                isOptionEqualToValue={(option, value) => option.RoleID === value.RoleID}
                onChange={(e, value) => setFilters({ ...filters, roleIds: value.map(v => v.RoleID) })}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.RoleDisplayName}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Role(s)..."
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingOptions ? <CircularProgress size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                disabled={isLoadingOptions}
                className={styles.filterInput}
              />
            ) : (
              <Autocomplete
                options={roles}
                loading={isLoadingOptions}
                getOptionLabel={(option) => option.RoleDisplayName}
                isOptionEqualToValue={(option, value) => option.RoleID === value.RoleID}
                onChange={(e, value) => setFilters({ ...filters, roleId: value?.RoleID })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Role..."
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingOptions ? <CircularProgress size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                disabled={isLoadingOptions}
                className={styles.filterInput}
              />
            )}
          </div>
        );

      case 'certification':
        return (
          <div key={index} className={styles.filterGroup}>
            <label className={styles.filterLabel}>{filterLabel}</label>
            <Autocomplete
              multiple
              options={certifications}
              loading={isLoadingOptions}
              disableCloseOnSelect
              getOptionLabel={(option) => option.Name}
              isOptionEqualToValue={(option, value) => option.CertificationTypeID === value.CertificationTypeID}
              onChange={(e, value) => setFilters({ ...filters, certificationIds: value.map(v => v.CertificationTypeID) })}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.Name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Certification(s)..."
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingOptions ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              disabled={isLoadingOptions}
              className={styles.filterInput}
            />
          </div>
        );

      case 'region':
        return (
          <div key={index} className={styles.filterGroup}>
            <label className={styles.filterLabel}>{filterLabel}</label>
            {filter.multiple ? (
              <Autocomplete
                multiple
                options={filter.options}
                value={filters.regionIds || []}
                disableCloseOnSelect
                getOptionLabel={(option) => `Region ${option}`}
                isOptionEqualToValue={(option, value) => option === value}
                onChange={(e, value) => setFilters({ ...filters, regionIds: value })}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    Region {option}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select Region(s)..." size="small" />
                )}
                className={styles.filterInput}
              />
            ) : (
              <FormControl size="small" className={styles.filterInput}>
                <Select
                  value={filters.regionId || ''}
                  onChange={(e) => setFilters({ ...filters, regionId: e.target.value })}
                  displayEmpty
                >
                  <MenuItem value="" disabled>Select Region...</MenuItem>
                  {filter.options.map(region => (
                    <MenuItem key={region} value={region}>Region {region}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <main>
      {/* Blue header */}
      <div className="full-row light-blue">
        <div className="container-fluid">
          <div className="row text-white pt-3 pb-3">
            <div className="col-md-12 text-white">
              <h1 className="text-uppercase">{reportName}</h1>
              <p>{reportDescription}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="full-row white">
        <div className="container-fluid">
          <div className={styles.content}>

            {/* Back button */}
            <Box className={styles.buttonContainer}>
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => navigate('/admin/reporting')}
              >
                Back
              </Button>
            </Box>

            {/* Filters + Run Buttons */}
            <Paper className={styles.actionPanel} elevation={0}>
            {reportConfig.filters && reportConfig.filters.length > 0 && (
              <div className={styles.filtersSection}>
                {reportConfig.filters.map((filter, index) => renderFilter(filter, index))}
              </div>
            )}

            {/* Run Report + Export Buttons */}
            <Box className={styles.runSection}>
              <Button
                variant="contained"
                onClick={runReport}
                disabled={isGenerating}
                className={styles.runButton}
              >
                {isGenerating ? <><CircularProgress size={18} color="inherit" style={{ marginRight: 8 }} /> Running...</> : 'Run Report'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => downloadReport('csv')}
                disabled={isGenerating || !hasRunReport || reportData.length === 0}
                className={styles.exportButton}
              >
                Export CSV
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => downloadReport('excel')}
                disabled={isGenerating || !hasRunReport || reportData.length === 0}
                className={styles.exportButton}
              >
                Export Excel
              </Button>
            </Box>
            </Paper>

            {/* Results */}
            {hasRunReport && (
              <>
                {reportData.length === 0 ? (
                  <div className={styles.noData}>
                    <p>No data found for this report. Try selecting different filters or verify that the selected event/role has associated records.</p>
                  </div>
                ) : (
                  <>
                    {/* DataGrid */}
                    <Paper className={styles.dataGridPaper}>
                      <DataGrid
                        rows={paginatedData}
                        columns={columns}
                        getRowId={(row) => row.__rowId}
                        autoHeight
                        hideFooterPagination
                        sortingMode="server"
                        sortModel={sortModel}
                        onSortModelChange={(m) => {
                          setSortModel(m);
                          setPaginationModel((prev) => ({ ...prev, page: 0 }));
                        }}
                        disableColumnFilter
                        disableDensitySelector
                        disableSelectionOnClick
                        disableMultipleSelection
                        slots={{ toolbar: null }}
                        className="reportRunDataGrid"
                      />
                    </Paper>

                    {/* Custom Pagination */}
                    <Paper className={styles.paginationPaper}>
                      <Stack
                        className={styles.paginationStack}
                        direction={{ xs: "column", md: "row" }}
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={2}
                        sx={{ width: '100%' }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Showing {totalCount > 0 ? startIndex + 1 : 0} to {Math.min(startIndex + paginationModel.pageSize, totalCount)} of {totalCount} records
                        </Typography>

                        <Stack className={styles.paginationControls} direction="row" alignItems="center">
                          <Pagination
                            count={pageCount}
                            page={paginationModel.page + 1}
                            onChange={(_, page) => setPaginationModel(prev => ({ ...prev, page: page - 1 }))}
                            color="primary"
                            variant="outlined"
                            shape="rounded"
                            showFirstButton
                            showLastButton
                            siblingCount={isMobile ? 0 : 1}
                            boundaryCount={1}
                            className="reportRunPagination"
                          />
                        </Stack>

                        <Box className={styles.pageSizeSelector}>
                          <Typography variant="body2" color="text.secondary">Show:</Typography>
                          <TextField
                            select
                            size="small"
                            value={paginationModel.pageSize}
                            onChange={(e) => setPaginationModel(prev => ({ ...prev, pageSize: parseInt(e.target.value), page: 0 }))}
                            SelectProps={{ native: true }}
                            className={styles.pageSizeField}
                          >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </TextField>
                          <Typography variant="body2" color="text.secondary">per page</Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </>
                )}
              </>
            )}

          </div>
        </div>
      </div>
    </main>
  );
};

export default ReportRunPage;
